import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

// 动态获取API基础URL - 根据环境自动适配
// 开发环境：使用 localhost:5001
// 生产环境：使用相对路径（空字符串），自动适配当前域名
const getBaseUrl = () => {
  return getApiBaseUrl();
};

// 创建通用API客户端（不带区域前缀）
const createCommonApiClient = () => {
  const client = axios.create({
    baseURL: getBaseUrl(),
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // 响应拦截器
  client.interceptors.response.use(
    response => {
      return response.data;
    },
    error => {
      console.error('API请求错误:', error);
      return Promise.reject(error);
    }
  );

  return client;
};

// 创建API客户端
const createApiClient = (zone = 'zone1') => {
  const baseURL = `${getBaseUrl()}/api/${zone}`;
  console.log('[DEBUG] 创建API客户端:', {
    baseURL,
    zone
  });
  
  const client = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // 请求拦截器
  client.interceptors.request.use(
    config => {
      // 添加认证token
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // 记录请求URL
      console.log('[DEBUG] 发送请求:', {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        fullPath: `${config.baseURL}${config.url}`,
        hasToken: !!token
      });
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  client.interceptors.response.use(
    response => {
      return response.data;
    },
    error => {
      console.error('API请求错误:', error);
      return Promise.reject(error);
    }
  );

  return client;
};

// 创建默认区域的API客户端
const defaultApiClient = createApiClient();

// 创建通用API客户端
const commonApiClient = createCommonApiClient();

// 创建特定区域的API客户端
const getZoneApiClient = (zone) => createApiClient(zone);

// 导出API方法
export const api = {
  // 系统相关
  system: {
    // 获取所有区域
    getAllZones: () => commonApiClient.get('/api/zones'),
    // 创建新区域
    // 接收区域配置信息，包括code, name, description, database等
    // 用于添加新的比赛区域（俱乐部）
    createZone: (data) => commonApiClient.post('/api/zones', data),
    // 测试数据库连接
    // 接收数据库URI信息，验证是否能成功连接
    // 用于在创建区域前确认数据库配置正确
    testConnection: (data) => commonApiClient.post('/api/zones/test-connection', data)
  },

  // 球员相关
  players: {
    getAll: (zone) => defaultApiClient.get(`/api/${zone}/players`),
    getById: (zone, id) => defaultApiClient.get(`/api/${zone}/players/${id}`),
    create: (zone, data) => defaultApiClient.post(`/api/${zone}/players`, data),
    update: (zone, id, data) => defaultApiClient.put(`/api/${zone}/players/${id}`, data),
    delete: (zone, id) => defaultApiClient.delete(`/api/${zone}/players/${id}`),
    getMatches: (zone, id) => defaultApiClient.get(`/api/${zone}/players/${id}/matches`),
    getWinRate: (zone, id) => defaultApiClient.get(`/api/${zone}/players/${id}/win-rate`),
    getRatingHistory: (zone, id) => defaultApiClient.get(`/api/${zone}/players/${id}/rating-history`),
    getDoublesPartners: (zone, id) => defaultApiClient.get(`/api/${zone}/players/${id}/doubles-partners`),
    getRankings: (zone, type = 'singles', includeAll = true) => defaultApiClient.get(`/api/${zone}/players/rankings?type=${type}&include_all=${includeAll}`),
    resetStats: (zone) => defaultApiClient.post(`/api/${zone}/players/reset-stats`)
  },

  // 比赛相关
  matches: {
    getAll: (zone) => defaultApiClient.get(`/api/${zone}/matches`),
    getById: (zone, id) => defaultApiClient.get(`/api/${zone}/matches/${id}`),
    create: (zone, data) => defaultApiClient.post(`/api/${zone}/matches`, data),
    update: (zone, id, data) => defaultApiClient.put(`/api/${zone}/matches/${id}`, data),
    delete: (zone, id) => defaultApiClient.delete(`/api/${zone}/matches/${id}`)
  },

  // 用户相关
  users: {
    getAll: () => defaultApiClient.get('/users'),
    getById: (id) => defaultApiClient.get(`/users/${id}`),
    create: (data) => defaultApiClient.post('/users', data),
    update: (id, data) => defaultApiClient.put(`/users/${id}`, data),
    delete: (id) => defaultApiClient.delete(`/users/${id}`)
  },

    // 活动相关
    events: {
      getAll: (zone) => defaultApiClient.get(`/api/${zone}/events`),
      getById: (zone, id) => defaultApiClient.get(`/api/${zone}/events/${id}`),
      create: (zone, data) => defaultApiClient.post(`/api/${zone}/events`, data),
      update: (zone, id, data) => defaultApiClient.put(`/api/${zone}/events/${id}`, data),
      delete: (zone, id) => defaultApiClient.delete(`/api/${zone}/events/${id}`),
      settle: (zone, id) => defaultApiClient.post(`/api/${zone}/events/${id}/settle`),
      reset: (zone, id) => defaultApiClient.post(`/api/${zone}/events/${id}/reset-settlement`),
      getMatches: (zone, id) => defaultApiClient.get(`/api/${zone}/events/${id}/matches`),
      addMatch: (zone, id, data) => defaultApiClient.post(`/api/${zone}/events/${id}/matches`, data),
      getSettled: (zone) => defaultApiClient.get(`/api/${zone}/events/settled`),
      resetAll: (zone) => defaultApiClient.post(`/api/${zone}/events/reset-all`)
    },

    // 场地管理相关
    courtUsage: {
      // 场地管理
      courts: {
        getAll: () => commonApiClient.get('/api/court-usage/courts'),
        getById: (id) => commonApiClient.get(`/api/court-usage/courts/${id}`),
        create: (data) => commonApiClient.post('/api/court-usage/courts', data),
        update: (id, data) => commonApiClient.put(`/api/court-usage/courts/${id}`, data),
        delete: (id) => commonApiClient.delete(`/api/court-usage/courts/${id}`)
      },
      // 大账户管理
      mainAccount: {
        getOverview: (params) => commonApiClient.get('/api/court-usage/main-account/overview', { params }),
        getRecords: (params) => commonApiClient.get('/api/court-usage/main-account/records', { params }),
        recharge: (data) => commonApiClient.post('/api/court-usage/main-account/recharge', data),
        consume: (data) => commonApiClient.post('/api/court-usage/main-account/consume', data),
        updateRecord: (id, data) => commonApiClient.put(`/api/court-usage/main-account/records/${id}`, data),
        deleteRecord: (id) => commonApiClient.delete(`/api/court-usage/main-account/records/${id}`)
      },
      // 小账户管理
      subAccounts: {
        getAll: () => commonApiClient.get('/api/court-usage/sub-accounts'),
        getById: (id) => commonApiClient.get(`/api/court-usage/sub-accounts/${id}`),
        create: (data) => commonApiClient.post('/api/court-usage/sub-accounts', data),
        update: (id, data) => commonApiClient.put(`/api/court-usage/sub-accounts/${id}`, data),
        delete: (id) => commonApiClient.delete(`/api/court-usage/sub-accounts/${id}`),
        getRecords: (id, params) => commonApiClient.get(`/api/court-usage/sub-accounts/${id}/records`, { params }),
        allocate: (id, data) => commonApiClient.post(`/api/court-usage/sub-accounts/${id}/allocate`, data),
        recharge: (id, data) => commonApiClient.post(`/api/court-usage/sub-accounts/${id}/recharge`, data),
        consume: (id, data) => commonApiClient.post(`/api/court-usage/sub-accounts/${id}/consume`, data),
        adjust: (id, data) => commonApiClient.post(`/api/court-usage/sub-accounts/${id}/adjust`, data)
      },
      // 统计
      statistics: {
        getStatistics: (params) => commonApiClient.get('/api/court-usage/statistics', { params }),
        exportExcel: (params) => commonApiClient.get('/api/court-usage/export/excel', { params, responseType: 'blob' })
      }
    }
  };

// 导出区域API客户端工厂
export const getZoneApi = (zone) => {
  const client = getZoneApiClient(zone);
  const AI_SUMMARY_TIMEOUT_MS = 120000;
  
  return {
    // 球员相关
    players: {
      getAll: () => client.get('/players'),
      getById: (id) => client.get(`/players/${id}`),
      create: (data) => client.post('/players', data),
      update: (id, data) => client.put(`/players/${id}`, data),
      delete: (id) => client.delete(`/players/${id}`),
      getMatches: (id) => client.get(`/players/${id}/matches`),
      getWinRate: (id) => client.get(`/players/${id}/win-rate`),
      getRatingHistory: (id) => client.get(`/players/${id}/rating-history`),
      getDoublesPartners: (id) => client.get(`/players/${id}/doubles-partners`),
      getDoublesStatistics: (id, options = {}) => {
        const params = new URLSearchParams();
        if (options.includePartners !== undefined) params.append('includePartners', options.includePartners);
        if (options.includeOpponents !== undefined) params.append('includeOpponents', options.includeOpponents);
        if (options.topN !== undefined) params.append('topN', options.topN);
        const queryString = params.toString();
        return client.get(`/players/${id}/statistics/doubles${queryString ? '?' + queryString : ''}`);
      },
      getRankings: (type = 'singles', includeAll = true) => client.get(`/players/rankings?type=${type}&include_all=${includeAll}`),
      resetStats: () => client.post('/players/reset-stats')
    },

    // 比赛相关
    matches: {
      getAll: () => client.get('/matches'),
      getById: (id) => client.get(`/matches/${id}`),
      create: (data) => client.post('/matches', data),
      update: (id, data) => client.put(`/matches/${id}`, data),
      delete: (id) => client.delete(`/matches/${id}`),
      // 标签相关
      getTags: (id) => client.get(`/matches/${id}/tags`),
      addMatchTag: (id, data) => client.post(`/matches/${id}/tags/match`, data),
      removeMatchTag: (id, tag, params) => {
        const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
        return client.delete(`/matches/${id}/tags/match/${encodeURIComponent(tag)}${queryString}`);
      },
      addPlayerTag: (id, data) => client.post(`/matches/${id}/tags/player`, data),
      removePlayerTag: (id, tag, params) => {
        const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
        return client.delete(`/matches/${id}/tags/player/${encodeURIComponent(tag)}${queryString}`);
      },
      updatePlayerTagVisibility: (id, tag, data) => client.patch(`/matches/${id}/tags/player/${encodeURIComponent(tag)}`, data)
    },

    // 用户相关
    users: {
      getAll: () => client.get('/users'),
      getById: (id) => client.get(`/users/${id}`),
      create: (data) => client.post('/users', data),
      update: (id, data) => client.put(`/users/${id}`, data),
      delete: (id) => client.delete(`/users/${id}`)
    },

    // 活动相关
    events: {
      getAll: () => client.get('/events'),
      getById: (id) => client.get(`/events/${id}`),
      create: (data) => client.post('/events', data),
      update: (id, data) => client.put(`/events/${id}`, data),
      delete: (id) => client.delete(`/events/${id}`),
      settle: (id) => client.post(`/events/${id}/settle`),
      reset: (id) => client.post(`/events/${id}/reset-settlement`),
      getMatches: (id) => client.get(`/events/${id}/matches`),
      addMatch: (id, data) => client.post(`/events/${id}/matches`, data),
      generateMatches: (id, data) => client.post(`/events/${id}/generate-matches`, data),
      getSettled: () => client.get('/events/settled'),
      resetAll: () => client.post('/events/reset-all'),
      // AI总结相关
      summary: {
        preview: (eventId, data = {}) => client.post(
          `/ai/events/${eventId}/summary/preview`,
          data,
          { timeout: AI_SUMMARY_TIMEOUT_MS }
        ),
        confirm: (eventId, data = {}) => client.post(
          `/ai/events/${eventId}/summary/confirm`,
          data,
          { timeout: AI_SUMMARY_TIMEOUT_MS }
        ),
        get: (eventId) => client.get(`/ai/events/${eventId}/summary`),
        delete: (eventId) => client.delete(`/ai/events/${eventId}/summary`)
      }
    },

    // 统计管理相关
    statistics: {
      recalculate: () => client.post('/statistics/recalculate'),
      clear: () => client.post('/statistics/clear'),
      restore: () => client.post('/statistics/restore')
    }
  };
}; 